import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  IoBarcodeOutline,
  IoLayersOutline,
  IoCalendarOutline,
  IoStorefrontOutline,
  IoTimeOutline,
  IoDocumentTextOutline,
  IoPricetagOutline,
  IoCashOutline,
  IoPencil,
  IoTrashOutline,
  IoClose,
} from "react-icons/io5";
import api, { fileUrl } from "@/api";
import { useI18n, STORE_LABELS } from "@/i18n";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { formatDateIT } from "@/utils";

const InfoRow = ({ Icon, label, value }) => (
  <div className="tf-info-row" data-testid={`info-${label}`}>
    <div className="tf-info-icon"><Icon size={20} /></div>
    <div className="tf-info-body">
      <div className="tf-info-label">{label}</div>
      <div className="tf-info-value">{value ?? "—"}</div>
    </div>
  </div>
);

export default function ExpiryDetailPage() {
  const { expiryId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [ex, setEx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/expiries/${expiryId}`);
      setEx(data);
    } catch (err) {
      console.error("Expiry load failed:", err);
      toast.error("Scadenza non trovata");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [expiryId, navigate]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    try {
      await api.delete(`/expiries/${expiryId}`);
      toast.success("Eliminato");
      navigate(-1);
    } catch { toast.error("Errore"); }
  };

  if (loading || !ex) {
    return (
      <div className="mobile-frame">
        <div className="tf-header-simple">
          <button className="tf-close-btn" onClick={() => navigate(-1)}><IoClose size={22} /></button>
          <div className="tf-header-title-center">…</div>
          <div style={{ minWidth: 44 }} />
        </div>
        <div className="tf-empty">{t("loading")}</div>
      </div>
    );
  }

  const imgSrc = ex.image_file_id ? fileUrl(ex.image_file_id) : (ex.image_url || "");
  const insertedDate = formatDateIT(ex.created_at);
  const expiryDisplay = formatDateIT(ex.expiry_date);

  return (
    <div className="mobile-frame" data-testid="expiry-detail-page">
      <div className="tf-header-simple">
        <button data-testid="ed-close" className="tf-close-btn" onClick={() => navigate("/expiries")} aria-label={t("cancel")}>
          <IoClose size={22} />
        </button>
        <div className="tf-header-title-center" data-testid="page-title">{ex.product_name}</div>
        <div style={{ minWidth: 44 }} />
      </div>

      <div style={{ padding: "0 20px 12px" }}>
        <span className="tf-priority tf-priority-high" style={{ fontSize: 12 }}>{t("expired_status")}</span>
      </div>

      <div className="tf-content" style={{ paddingTop: 4 }}>
        <InfoRow Icon={IoBarcodeOutline} label={t("barcode")} value={ex.barcode || "—"} />
        <InfoRow Icon={IoLayersOutline} label={t("quantity_available")} value={t("quantity_pieces").replace("$", ex.quantity)} />
        <InfoRow Icon={IoCalendarOutline} label={t("expiry_date")} value={expiryDisplay} />
        <InfoRow Icon={IoStorefrontOutline} label={t("stores")} value={t(STORE_LABELS[ex.store_id])} />
        <InfoRow Icon={IoTimeOutline} label={t("date_inserted")} value={insertedDate} />
        {ex.cost != null && (
          <InfoRow
            Icon={IoCashOutline}
            label={t("cost")}
            value={`€ ${Number(ex.cost).toFixed(2)}  ·  ${t("total")}: € ${(Number(ex.cost) * Number(ex.quantity || 0)).toFixed(2)}`}
          />
        )}
        {ex.price != null && (
          <InfoRow
            Icon={IoPricetagOutline}
            label={t("price")}
            value={`€ ${Number(ex.price).toFixed(2)}  ·  ${t("total")}: € ${(Number(ex.price) * Number(ex.quantity || 0)).toFixed(2)}`}
          />
        )}
        <InfoRow Icon={IoDocumentTextOutline} label={t("destination")} value={ex.destination || "—"} />
        {ex.notes && <InfoRow Icon={IoDocumentTextOutline} label={t("notes")} value={ex.notes} />}

        <div className="tf-stack" style={{ marginTop: 24 }}>
          <button data-testid="ed-edit" className="tf-btn-outline-red" onClick={() => navigate(`/expiry/${expiryId}/edit`)}>
            <IoPencil size={18} />
            {t("edit_article")}
          </button>
          <button data-testid="ed-delete" className="tf-btn-primary" onClick={() => setConfirmOpen(true)}>
            <IoTrashOutline size={18} />
            {t("delete_article")}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={remove}
        title={t("confirm_delete")}
        description={ex.product_name}
        confirmLabel={t("delete")}
      />
    </div>
  );
}
